import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { productionBatchService } from "~/services/production";
import type {
  BatchCompletionData,
  BatchIngredientOverride,
  ProductionBatchFilters,
  ProductionBatchFormData,
} from "~/types/production";

const QUERY_KEY = "production_batches";
const RECIPES_KEY = "recipes";

export function useProductionBatches(filters?: ProductionBatchFilters) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: () => productionBatchService.getAll(filters),
  });
}

export function useProductionBatch(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => productionBatchService.getById(id!),
    enabled: !!id,
  });
}

export function useBatchIngredientCosts(batchId: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, batchId, "ingredient_costs"],
    queryFn: () => productionBatchService.getIngredientCosts(batchId!),
    enabled: !!batchId,
  });
}

export function useProductionBatchAccounting(batchId: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, batchId, "accounting"],
    queryFn: () => productionBatchService.getAccounting(batchId!),
    enabled: !!batchId,
  });
}

export function useRegisterProductionBatchAccounting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      batchId,
      createdBy,
    }: {
      batchId: string;
      createdBy?: string | null;
    }) => productionBatchService.registerAccounting(batchId, createdBy),
    onSuccess: (_data, { batchId }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, batchId, "accounting"],
      });
    },
  });
}

export function useProductionBatchStatistics(filters?: {
  start_date_from?: string;
  start_date_to?: string;
}) {
  return useQuery({
    queryKey: [QUERY_KEY, "statistics", filters],
    queryFn: () => productionBatchService.getStatistics(filters),
  });
}

export function useCreateProductionBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      createdBy,
    }: {
      data: ProductionBatchFormData;
      createdBy: string;
    }) => productionBatchService.create(data, createdBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateProductionBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ProductionBatchFormData>;
    }) => productionBatchService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

export function useUpdateBatchExpiry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, expiryDate }: { id: string; expiryDate: string }) =>
      productionBatchService.updateExpiry(id, expiryDate),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

export function useUpdateBatchIngredient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      batchId,
      ingredientId,
      data,
    }: {
      batchId: string;
      ingredientId: string;
      data: { planned_quantity?: number; notes?: string };
    }) => productionBatchService.updateIngredient(batchId, ingredientId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, variables.batchId],
      });
    },
  });
}

export function useUpdateBatchIngredients() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      batchId,
      overrides,
      targetQuantity,
    }: {
      batchId: string;
      overrides: BatchIngredientOverride[];
      targetQuantity: number;
    }) =>
      productionBatchService.updateIngredients(
        batchId,
        overrides,
        targetQuantity
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, variables.batchId],
      });
      // Invalidate stock-related queries
      queryClient.invalidateQueries({ queryKey: ["oil_types"] });
      queryClient.invalidateQueries({ queryKey: ["raw_materials"] });
    },
  });
}

export function useStartProductionBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productionBatchService.start(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, id] });
    },
  });
}

export function useCompleteProductionBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      completionData,
    }: {
      id: string;
      completionData: BatchCompletionData;
    }) => productionBatchService.complete(id, completionData),
    onSuccess: (_, variables) => {
      // Invalidate batches
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
      // Invalidate stock-related queries
      queryClient.invalidateQueries({ queryKey: ["oil_types"] });
      queryClient.invalidateQueries({ queryKey: ["raw_materials"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useCancelProductionBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productionBatchService.cancel(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, id] });
    },
  });
}

export function useGenerateBatchNumber() {
  return useMutation({
    mutationFn: ({
      productCode,
      startDate,
      endDate,
    }: {
      productCode: string;
      startDate: Date | string;
      endDate: Date | string;
    }) =>
      productionBatchService.generateBatchNumber(
        productCode,
        startDate,
        endDate
      ),
  });
}
